<template>
  <div :data-cy="`cf-input-${question.id}`" class="max-w-md">
    <!-- text -->
    <InputField
      v-if="type === 'text'"
      :model-value="displayValue"
      :placeholder="question.unit || 'Answer'"
      data-cy="cf-input-text"
      @update:model-value="(v) => typeText(v)"
    />

    <!-- number: InputField rather than NumberInput, whose modelValue is a
         non-null number and so cannot represent an unanswered question. -->
    <div v-else-if="type === 'number'" class="flex items-center gap-2">
      <InputField
        :model-value="displayValue"
        type="number"
        placeholder="0"
        data-cy="cf-input-number"
        @update:model-value="(v) => typeNumber(v)"
      />
      <span v-if="question.unit" class="text-sm text-gray-500">
        {{ question.unit }}
      </span>
    </div>

    <Datepicker
      v-else-if="type === 'date'"
      :model-value="dateForPicker"
      data-cy="cf-input-date"
      @update:model-value="(v) => emitDate(v)"
    />

    <Checkbox
      v-else-if="type === 'checkbox'"
      :model-value="answer?.value === true"
      :label="question.title"
      data-cy="cf-input-checkbox"
      @update:model-value="(v) => emitValue(v)"
    />

    <!-- star_rating: 1-5, and clicking the current rating clears it, since the
         question may be optional and there is no other way back to unanswered. -->
    <div v-else-if="type === 'star_rating'" class="flex items-center gap-1">
      <button
        v-for="star in 5"
        :key="star"
        type="button"
        class="text-xl leading-none"
        :class="
          star <= Number(answer?.value ?? 0)
            ? 'text-yellow-500'
            : 'text-gray-300'
        "
        :aria-label="`${star} of 5`"
        :aria-pressed="star === Number(answer?.value ?? 0)"
        :data-cy="`cf-input-star-${star}`"
        @click="emitValue(star === Number(answer?.value ?? 0) ? null : star)"
      >
        ★
      </button>
    </div>

    <!-- Every token type goes through one control; only the option list differs. -->
    <SelectInput
      v-else-if="isTokenType(type)"
      :model-value="
        type === 'multi_select' ? selectedTokens : selectedTokens[0]
      "
      :options="tokenOptions"
      :multiselect="type === 'multi_select'"
      placeholder="Choose"
      data-cy="cf-input-select"
      @update:model-value="(v) => emitTokens(v)"
    />

    <!-- Signature. The admin portal never draws one: a signature is the
         customer's mark, and a pad here would let the office manufacture it. The
         only action offered is clearing a captured one so the customer signs
         again — which is also the only signature change WriteAnswer permits.
         The image is rendered here rather than by the row, because supplying this
         slot replaces the row's own answer render. -->
    <div v-else-if="type === 'signature'" data-cy="cf-input-signature">
      <div v-if="answer?.signature" class="flex items-start gap-3">
        <img
          :src="answer.signature.image_url"
          alt="Captured signature"
          class="h-16 border border-gray-200 bg-white"
        />
        <button
          type="button"
          class="text-sm text-blue-600 hover:underline"
          data-cy="cf-clear-signature"
          @click="emitValue(null)"
        >
          Clear signature
        </button>
      </div>
      <p v-else class="text-sm text-gray-500">
        The customer signs this in the field.
      </p>
    </div>

    <!-- photo_only carries its answer entirely in the photo set, so the picker
         below is the whole control and this fallback would contradict it. -->
    <p
      v-else-if="!allowsPhotos"
      class="text-sm text-gray-500"
      data-cy="cf-input-unsupported"
    >
      This question type cannot be answered here.
    </p>

    <button
      v-if="allowsPhotos"
      type="button"
      class="mt-2 text-sm text-blue-600 hover:underline"
      :data-cy="`cf-photos-button-${question.id}`"
      @click="showPhotoPicker = true"
    >
      {{ photoButtonLabel }}
    </button>

    <AttachAnswerPhotosModal
      v-if="showPhotoPicker"
      :question="question"
      :answer="answer"
      @hide-modal="showPhotoPicker = false"
      @submit="applyPhotoIds"
    />

    <p
      v-if="state === 'conflict'"
      class="mt-1 text-xs text-red-700"
      :data-cy="`cf-conflict-${question.id}`"
    >
      Someone else changed this answer. Reloaded with their version — re-enter
      yours if it still applies.
    </p>
    <p
      v-else-if="state === 'saving'"
      class="mt-1 text-xs text-gray-500"
      :data-cy="`cf-saving-${question.id}`"
    >
      Saving…
    </p>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";

import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import Datepicker from "@/components/Seranova/Datepicker/Datepicker.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import type {
  CustomFormAnswer,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";
import AttachAnswerPhotosModal from "./AttachAnswerPhotosModal.vue";
import { answerFieldType, isTokenType } from "./customFormAnswerDisplay";
import { SYSTEM_TOKEN_LABELS } from "./customFormFields";
import type { SaveState } from "./useAnswerSave";

// The editable counterpart to the read-only row, dropped into its #answer slot.
// It owns only the mapping from field_type to a control and to the attribute the
// answers API expects — the caller owns saving, so lock_version and the question
// id stay in one place rather than being threaded through every input.
const props = withDefaults(
  defineProps<{
    question: CustomFormSubmissionQuestion;
    answer: CustomFormAnswer | null;
    state?: SaveState;
    // Whether a write for this question is still queued or in flight. Owned by the
    // caller because it is the caller that saves; see draft below for why a
    // free-text control needs to know.
    pending?: boolean;
  }>(),
  { state: "idle", pending: false }
);

const ISO_DATE = "YYYY-MM-DD";
const PICKER_DATE = "MM/DD/YY";
const PICKER_FORMATS = [
  PICKER_DATE,
  "M/D/YY",
  "MM/DD/YYYY",
  "M/D/YYYY",
  ISO_DATE
];

const emit = defineEmits<{
  update: [
    payload: { attributes: Record<string, unknown>; immediate: boolean }
  ];
}>();

// The answer's snapshotted type where there is one, for the same reason the
// read-only row uses it: a published question's type can still be edited, and a
// historical value must not be reinterpreted under the new type.
const type = computed(() => answerFieldType(props.question, props.answer));

const stringValue = computed(() =>
  props.answer?.value === null || props.answer?.value === undefined
    ? ""
    : String(props.answer.value)
);

// What has been typed since the last settled write, and the reason the free-text
// controls bind to displayValue rather than straight to the stored answer.
// InputField is fully controlled, and the caller adopts the server's answer the
// moment a response lands — so a response that arrives mid-typing would put the
// field back to the value it carried and drop everything typed after it. Worse
// than a flicker: the next keystroke then builds on the value that was restored.
//
// Held until nothing is outstanding rather than until the value matches, because
// the server legitimately returns something else — a 409 reload, or a normalized
// number — and those must win once the typing has actually been saved.
const draft = ref<string | null>(null);

const displayValue = computed(() => draft.value ?? stringValue.value);

watch(
  () => props.pending,
  (writing) => {
    if (!writing) draft.value = null;
  }
);

const selectedTokens = computed(() => props.answer?.selected_tokens ?? []);

const showPhotoPicker = ref(false);

// The template author decides whether a question takes photos at all, so the
// picker follows that rather than the field type.
const allowsPhotos = computed(
  () => props.question.photo_requirement !== "no_photos"
);

const attachedPhotoIds = computed(() =>
  (props.answer?.photos ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((photo) => photo.id)
);

const photoButtonLabel = computed(() => {
  const count = attachedPhotoIds.value.length;
  const min = props.question.photo_min_count;
  if (!count) return min > 0 ? `Add photos (${min} required)` : "Add photos";

  return `Photos (${count})`;
});

// An author's own option pool, or the fixed vocabulary for the two system-owned
// types — the same map the read-only row labels from, so the two cannot disagree.
const tokenOptions = computed(() => {
  const authored = props.question.options ?? [];
  if (authored.length) {
    return authored.map((option) => ({
      value: option.token,
      label: option.label
    }));
  }

  const system = SYSTEM_TOKEN_LABELS[type.value] ?? {};
  return Object.entries(system).map(([token, label]) => ({
    value: token,
    label
  }));
});

// Datepicker's own format is MM/DD/YY; the contract's is ISO. Both directions are
// converted here, because handing it an ISO string makes it reformat on mount and
// emit — which is how a stored date got saved back as "7/30/26" and failed to
// parse into value_date at all.
const dateForPicker = computed(() => {
  const raw = props.answer?.value;
  if (!raw) return "";

  const parsed = moment(String(raw), ISO_DATE, true);
  return parsed.isValid() ? parsed.format(PICKER_DATE) : String(raw);
});

// The single write path. Nothing emits unless the value actually differs from what
// is stored: a control that echoes its value on mount (Datepicker does) would
// otherwise save on open, and "open the editor" must never be a write.
function emitIfChanged(
  attributes: Record<string, unknown>,
  { immediate = true } = {}
) {
  if (!isChanged(attributes)) return;

  emit("update", { attributes, immediate });
}

function isChanged(attributes: Record<string, unknown>): boolean {
  if ("selected_tokens" in attributes) {
    const next = (attributes.selected_tokens as string[]) ?? [];
    const current = props.answer?.selected_tokens ?? [];
    return (
      next.length !== current.length ||
      next.some((token, index) => token !== current[index])
    );
  }
  // Ordered: position is written from this list, so a reorder is a change.
  if ("photo_ids" in attributes) {
    const next = (attributes.photo_ids as number[]) ?? [];
    const current = attachedPhotoIds.value;
    return (
      next.length !== current.length ||
      next.some((id, index) => id !== current[index])
    );
  }
  if ("signature_base64" in attributes) {
    // Only ever a clear, and only meaningful while one is captured.
    return !!props.answer?.signature;
  }

  const current = props.answer?.value ?? null;
  return (attributes.value ?? null) !== current;
}

// A signature routes through signature_base64, not value: CustomFormAnswer#value=
// is a no-op for the signature type, so sending `value` would save nothing and
// the Clear button would appear to do something while changing nothing.
function emitValue(value: unknown, { immediate = true } = {}) {
  const attributes =
    type.value === "signature" ? { signature_base64: value } : { value };
  emitIfChanged(attributes, { immediate });
}

// The two free-text controls record what was typed before deciding whether it is
// worth a write. The draft is set even when nothing is emitted — a value equal to
// the stored one, or a half-typed number — since the field still shows it.
function typeText(incoming: unknown) {
  draft.value = String(incoming ?? "");
  emitValue(incoming, { immediate: false });
}

function typeNumber(incoming: unknown) {
  draft.value = String(incoming ?? "");
  emitNumber(incoming);
}

// Coerced, not cast: Vue's v-model applies looseToNumber on type="number", so
// InputField emits a number here despite declaring string — and a half-typed "-"
// comes back as a string. Trusting the declared type threw on .trim() and killed
// the write before it reached the saver.
//
// "" means the field was emptied, which is unanswered — not 0. NaN is a value
// mid-typing, not a cleared field: it serializes to null and would wipe the answer.
function emitNumber(incoming: unknown) {
  const trimmed = String(incoming ?? "").trim();
  if (trimmed === "") return emitIfChanged({ value: null });

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return;

  emitIfChanged({ value: parsed }, { immediate: false });
}

// A half-typed date is not a value: writing it would clear the stored one.
function emitDate(incoming: unknown) {
  const trimmed = String(incoming ?? "").trim();
  if (!trimmed) return emitIfChanged({ value: null });

  const parsed = moment(trimmed, PICKER_FORMATS, true);
  if (!parsed.isValid()) return;

  emitIfChanged({ value: parsed.format(ISO_DATE) });
}

// §17.6: photo_ids replaces the whole set in the given order, so the picker's
// list is sent verbatim -- including empty, which detaches everything.
function applyPhotoIds(photoIds: number[]) {
  showPhotoPicker.value = false;
  emitIfChanged({ photo_ids: photoIds });
}

function emitTokens(incoming: unknown) {
  const tokens = Array.isArray(incoming)
    ? incoming
    : [incoming].filter((token) => token !== null && token !== undefined);
  emitIfChanged({ selected_tokens: tokens });
}
</script>
